import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Drawer, Empty, Input, List, Space, Spin, Typography } from "antd";
import type { InputRef } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { fetchLookup } from "../../api/lookupApi";
import { useLookupPanelStore } from "../../stores/lookupPanelStore";
import { useEntityFormStore } from "../../stores/entityFormStore";

export function UniversalRightLookupPanel() {
  const { open, request, closePanel } = useLookupPanelStore();
  const openForm = useEntityFormStore((x) => x.openForm);
  const entityFormOpen = useEntityFormStore((x) => x.open);
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef<InputRef>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActiveIndex(0);
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [open, request?.entity]);

  const lookupUrl = useMemo(
    () => request ? resolveLookupUrl(request.lookupUrl, request.parentContext) : "",
    [request]
  );

  const query = useQuery({
    queryKey: ["lookup", request?.entity, lookupUrl, request?.parentContext],
    queryFn: () => request ? fetchLookup(lookupUrl) : Promise.resolve([]),
    enabled: open && !!request,
    staleTime: Infinity
  });

  const rows = useMemo(() => filterLookupRows(query.data ?? [], q), [query.data, q]);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(rows.length - 1, 0)));
  }, [rows.length]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 54,
    overscan: 8
  });

  const title = request?.title ?? "Select";
  const canCreate = !!request?.createModule;

  const closeAndReturnFocus = () => {
    const returnFocus = request?.returnFocus;
    closePanel();
    requestAnimationFrame(() => returnFocus?.());
  };

  const createNew = () => {
    if (!request?.createModule) return;
    const activeRequest = request;
    const createModule = request.createModule;
    closePanel();
    openForm({
      module: createModule,
      mode: "create",
      source: "lookup-panel",
      initialValues: { name: q },
      parentContext: activeRequest.parentContext,
      onSuccess: (saved: any) => {
        const data = saved?.data ?? saved;
        const item = {
          id: data?.id ?? data?.Id ?? data?.value ?? data?.Value ?? data,
          label: data?.name ?? data?.Name ?? data?.label ?? data?.Label ?? data?.accountNumber ?? data?.AccountNumber ?? data?.accountName ?? data?.AccountName ?? q,
          raw: data
        };
        void queryClient.invalidateQueries({ queryKey: ["lookup"] });
        activeRequest.suppressAfterSelect?.();
        closePanel();
        requestAnimationFrame(() => {
          activeRequest.onSelect(item);
          activeRequest.returnFocus?.();
        });
      }
    });
  };

  const selectRow = (row: typeof rows[number]) => {
    if (!request) return;
    const activeRequest = request;
    activeRequest.suppressAfterSelect?.();
    closePanel();
    requestAnimationFrame(() => {
      activeRequest.onSelect(row);
      activeRequest.returnFocus?.();
    });
  };

  const handleLookupKeyDown = (event: KeyboardEvent) => {
    if (!rows.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => {
        const next = Math.min(current + 1, rows.length - 1);
        virtualizer.scrollToIndex(next, { align: "auto" });
        return next;
      });
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => {
        const next = Math.max(current - 1, 0);
        virtualizer.scrollToIndex(next, { align: "auto" });
        return next;
      });
    }

    if (event.key === "Enter") {
      event.preventDefault();
      selectRow(rows[activeIndex]);
    }
  };

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <Drawer
      title={<Space><SearchOutlined />{title}</Space>}
      open={open}
      onClose={closeAndReturnFocus}
      width={420}
      zIndex={entityFormOpen ? 2600 : 1800}
      autoFocus={false}
      placement="right"
      className="universal-lookup-panel"
      extra={canCreate ? <Button size="small" type="primary" icon={<PlusOutlined />} onClick={createNew}>Create</Button> : null}
    >
      <Space direction="vertical" style={{ width: "100%" }} size={10}>
        <Input.Search ref={searchRef} autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={handleLookupKeyDown} placeholder="Type to search..." />
        <div className="lookup-context-line">
          <Typography.Text type="secondary">Search and select records.</Typography.Text>
        </div>
        {query.isLoading ? <Spin /> : rows.length === 0 ? (
          <Empty description="No matching items">
            {canCreate ? <Button type="primary" icon={<PlusOutlined />} onClick={createNew}>Create New</Button> : null}
          </Empty>
        ) : (
          <div ref={parentRef} className="lookup-virtual-list">
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualItems.map((item) => {
                const row = rows[item.index];
                return (
                  <div
                    key={item.key}
                    className={`lookup-row${item.index === activeIndex ? " is-active" : ""}`}
                    style={{ transform: `translateY(${item.start}px)` }}
                    onMouseEnter={() => setActiveIndex(item.index)}
                    onClick={() => selectRow(row)}
                  >
                    <List.Item.Meta title={row.label} description={row.subtitle} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Space>
    </Drawer>
  );
}

function resolveLookupUrl(url: string, parentContext?: Record<string, unknown>) {
  let next = url;
  Object.entries(parentContext ?? {}).forEach(([key, value]) => {
    next = next.replace(`{${key}}`, encodeURIComponent(String(value ?? "")));
  });
  if (next.includes("{value}")) {
    next = next.replace("{value}", encodeURIComponent(String(Object.values(parentContext ?? {})[0] ?? "")));
  }
  if (url.includes("entry-categories") && parentContext?.transactionType && !url.includes("transactionType")) {
    next += `${next.includes("?") ? "&" : "?"}transactionType=${encodeURIComponent(String(parentContext.transactionType))}`;
  }
  if (url.includes("parent-candidates") && parentContext?.accountType && !next.includes("accountType")) {
    next += `${next.includes("?") ? "&" : "?"}accountType=${encodeURIComponent(String(parentContext.accountType))}`;
  }
  if (url.includes("/ledgers/children") && parentContext?.parentLedgerId && !next.includes("parentId")) {
    next += `${next.includes("?") ? "&" : "?"}parentId=${encodeURIComponent(String(parentContext.parentLedgerId))}`;
  }
  if (url.includes("/banks/accounts") && parentContext?.bankId && !next.includes("bankId")) {
    next += `${next.includes("?") ? "&" : "?"}bankId=${encodeURIComponent(String(parentContext.bankId))}`;
  }
  return next;
}

function filterLookupRows(rows: Awaited<ReturnType<typeof fetchLookup>>, q: string) {
  const term = q.trim().toLowerCase();
  if (!term) return rows;

  return rows.filter((row) => {
    const rawValues = row.raw && typeof row.raw === "object"
      ? Object.values(row.raw as Record<string, unknown>)
      : [];
    return [row.label, row.subtitle, ...rawValues]
      .filter((value) => value !== undefined && value !== null)
      .some((value) => String(value).toLowerCase().includes(term));
  });
}
